import React, { useEffect, useState } from "react"
import { Col, Container, Row, Table } from "react-bootstrap"
import { Link } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { fetchAPIData, notAuthorized } from "../../api/AuthService"
import { FETCH_UNRELEASED } from "../../constant/Constants"
import { addBreadCrumbLinks } from "../commonComponents/EventHandler"
import "../main.css"

// Known common-page type codes — these route to /COM/{subType}/... instead of /preliminary/{type}/...
const COMMON_TYPE_CODES = new Set([
   "SPS", "TMS", "IPM", "ACS", "ATS", "ILST", "IAPS", "LIS",
   "MI", "PI", "PROS", "SP", "SOP", "RMPI", "QUAL", "CPP", "ARTCAD", "IRM"
])

// Extract short type code from item.
// Strategy 1: type field — handles "COM/IRM" -> "IRM", "COM/LIS" -> "LIS", "APMP" -> "APMP"
// Strategy 2: name prefix before first dash e.g. "APMP-0000" -> "APMP" (fallback when type absent)
// Strategy 3: acronym from multi-word type description e.g. "Intermediate Product Part" -> "IPP"
const getTypeCode = (item) => {
   if (item.type) {
      const t = item.type.trim()
      if (!t.includes(" ")) {
         // Strip leading "COM/" prefix: "COM/IRM" -> "IRM", "COM/LIS" -> "LIS"
         const slashIdx = t.lastIndexOf("/")
         return slashIdx >= 0 ? t.substring(slashIdx + 1).toUpperCase() : t.toUpperCase()
      }
      // Multi-word: build acronym e.g. "Intermediate Product Part" -> "IPP"
      return t.split(/\s+/).map((word) => word[0]).join("").toUpperCase()
   }
   if (item.name) {
      const dash = item.name.indexOf("-")
      if (dash > 0) return item.name.substring(0, dash).trim().toUpperCase()
   }
   return ""
}

const UnreleasedSearchResultDisp = ({ partName }) => {
   const navigate = useNavigate()
   const [loading, setLoading] = useState(false)
   const [results, setResults] = useState([])
   const [errorFlag, setErrorFlag] = useState(0)
   const [error, setError] = useState("")

   useEffect(() => {
      async function getUnreleasedData() {
         try {
            setLoading(true)
            setErrorFlag(0)
            setError("")

            const response = await fetchAPIData(`${FETCH_UNRELEASED}/${encodeURIComponent(partName)}`)
            const raw = response.data

            // Normalise: API may return a single object or an array
            const toArray = (val) => {
               if (!val) return []
               if (Array.isArray(val)) return val
               if (typeof val === "object" && (val.name || val.id)) return [val]
               if (typeof val === "object") {
                  const nested = Object.values(val).find((v) => Array.isArray(v))
                  if (nested) return nested
               }
               return []
            }

            const data = toArray(raw)

            if (data.length === 1) {
               // Single result — navigate directly to the detail page.
               // PreliminaryRouter picks the correct AllInfo component which then
               // calls /{type}-api/all-info/{id}/{name} and renders the full view.
               const item = data[0]
               const typeCode = getTypeCode(item)
               const id = item.id ? item.id.trim() : ""
               const name = item.name ? item.name.trim() : ""
               if (typeCode && id && name) {
                  const linkPath = COMMON_TYPE_CODES.has(typeCode)
                     ? `/preliminary/COM/${typeCode}/${encodeURIComponent(id)}/${encodeURIComponent(name)}`
                     : `/preliminary/${typeCode}/${encodeURIComponent(id)}/${encodeURIComponent(name)}`
                  addBreadCrumbLinks(name, linkPath)
                  // Replace /unreleasedData/... with /search/ in browser history directly,
                  // then navigate to preliminary — back button lands on /search/
                  window.history.replaceState(null, "", "/search/")
                  navigate(linkPath)
                  return
               }
            }

            if (data.length > 1) {
               setResults(data)
               setErrorFlag(1)
            } 
            else {
               setErrorFlag(2)
            }
            setLoading(false)
         } catch (error) {
            const status = error.response && error.response.data ? Number(error.response.data.status) : 0
            console.log("API error status:", status)
            if (status === 401) {
               notAuthorized(navigate)
               return
            }
            if (status === 403) {
               navigate("/NotAccess")
               return
            }
            if (status === 404) {
               setError("You do not have access to the object requested.")
            }
            if (status === 422) {
               setError("The CSS objects do not have the Preliminary data.")
            }
            setLoading(false)
            console.log("Error", `API request failed: ${status}`)
         }
      }
      getUnreleasedData()
   }, [partName, navigate])

   return (
      <React.Fragment>
         {loading && (
            <h2 style={{ textAlign: "center", color: "white" }}>
               <i className="fa fa-spinner fa-spin" style={{ marginLeft: "-4rem" }} />{" "}
               Loading........
            </h2>
         )}

         {error && (
            <Row className="labels">
               <Col md={{ span: 4, offset: 4 }}>
                  &nbsp; {error}
               </Col>
            </Row>
         )}

         {errorFlag === 2 && !error && (
            <Row className="labels">
               <Col md={{ span: 4, offset: 4 }}>
                  &nbsp; EAP is to be enabled to access the page requested.
               </Col>
            </Row>
         )}

         {errorFlag === 1 && (
            <Container fluid>
               <Row className="labels">
                  <Col>{results.length} Preliminary Record(s) Found.</Col>
               </Row>
               <Row>
                  <Col md="12" sm="12" style={{ marginTop: "0px" }}>
                     <Table striped bordered hover size="sm" className="tableStyle">
                        <thead>
                           <tr>
                              <th>Name</th>
                              <th>Object Type</th>
                              <th>ID</th>
                           </tr>
                        </thead>
                        <tbody>
                           {results.map((item, index) => {
                              const typeCode = getTypeCode(item)
                              const id = item.id ? item.id.trim() : ""
                              const name = item.name ? item.name.trim() : ""
                              const linkPath = typeCode && id && name
                                 ? COMMON_TYPE_CODES.has(typeCode)
                                    ? `/preliminary/COM/${typeCode}/${encodeURIComponent(id)}/${encodeURIComponent(name)}`
                                    : `/preliminary/${typeCode}/${encodeURIComponent(id)}/${encodeURIComponent(name)}`
                                 : null

                              return (
                                 <tr key={index}>
                                    <td>
                                       {linkPath ? (
                                          <Link
                                             style={{ textDecoration: "none", color: "#007bff" }}
                                             onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                                             onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                                             onClick={() => addBreadCrumbLinks(name, linkPath)}
                                             to={linkPath}
                                          >
                                             {name}
                                          </Link>
                                       ) : (
                                          <span>{name}</span>
                                       )}
                                    </td>
                                    <td>{item.type || item.objecttype || typeCode}</td>
                                    <td>{id}</td>
                                 </tr>
                              )
                           })}
                        </tbody>
                     </Table>
                  </Col>
               </Row>
            </Container>
         )}
      </React.Fragment>
   )
}

export default UnreleasedSearchResultDisp
